import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useAddBeneficiary } from '../hooks/beneficiaries/useAddBeneficiary';
import { useUpdateBeneficiary } from '../hooks/beneficiaries/useUpdateBeneficiary';
import { notifyUser } from '../util/util';
import { useModalWidth } from '../hooks/useModalWidth';

export const AddBeneficiaryModal = ({ isOpen, onClose, currentTotalPerc = 0, isEdit = false, beneficiaryToEdit = null, onEditSuccess }) => {
  const modalWidth = useModalWidth();
  const addBeneficiaryMutation = useAddBeneficiary();
  const updateBeneficiaryMutation = useUpdateBeneficiary();
  const [formState, setFormState] = useState({
    firstName: '',
    surname: '',
    otherNames: '',
    relationship: '',
    birthDate: '',
    percAlloc: 0,
    address: '',
  });

  useEffect(() => {
    if (isEdit && beneficiaryToEdit) {
      // Parse fullName into firstName, surname, otherNames
      // Rules:
      // - If name has exactly two words: firstName = first, surname = second, otherNames = ''
      // - If name has more than two words: firstName = first, surname = second, otherNames = the rest (third+)
      const nameParts = beneficiaryToEdit.name ? beneficiaryToEdit.name.trim().split(/\s+/) : [];
      let firstName = '';
      let surname = '';
      let otherNames = '';

      if (nameParts.length === 1) {
        firstName = nameParts[0];
      } else if (nameParts.length === 2) {
        firstName = nameParts[0];
        surname = nameParts[1];
      } else if (nameParts.length > 2) {
        firstName = nameParts[0];
        surname = nameParts[1];
        otherNames = nameParts.slice(2).join(' ');
      }

      setFormState({
        firstName,
        surname,
        otherNames,
        relationship: beneficiaryToEdit.relationship || '',
        birthDate: beneficiaryToEdit.dob ? dayjs(beneficiaryToEdit.dob).format('YYYY-MM-DD') : '',
        percAlloc: beneficiaryToEdit.percAllocValue || beneficiaryToEdit.percentage ? parseFloat(beneficiaryToEdit.percentage) || 0 : 0,
        address: beneficiaryToEdit.address || '',
      });
    } else {
      setFormState({
        firstName: '',
        surname: '',
        otherNames: '',
        relationship: '',
        birthDate: '',
        percAlloc: 0,
        address: '',
      });
    }
  }, [isEdit, beneficiaryToEdit, isOpen]);

  const omkTextFieldStyles = React.useMemo(() => ({
    width: '100%',
    '--omk-textfield-height': '48px',
    '--omk-textfield-border-radius': '10px',
    '--omk-textfield-padding': '0 16px',
    '--omk-textfield-background': '#fff',
    '--omk-textfield-border-color': '#ddd',
    '--omk-textfield-hover-border-color': '#0D7C4B',
  }), []);

  const handleFormChange = (field) => (event) => {
    const value = event?.target?.value ?? event;
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handlePercentChange = (event) => {
    const value = event?.target?.value ?? event;
    const numericValue = Math.min(maxPerc, Math.max(0, Number(value) || 0));
    setFormState((prev) => ({ ...prev, percAlloc: numericValue }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formState.firstName.trim() || !formState.surname.trim()) {
      notifyUser('error', 'Please enter first name and surname');
      return;
    }
    if (!formState.relationship) {
      notifyUser('error', 'Please select the relationship');
      return;
    }
    if (!formState.birthDate) {
      notifyUser('error', 'Please select the birth date');
      return;
    }
    if (formState.percAlloc <= 0) {
      notifyUser('error', 'Percentage must be greater than 0');
      return;
    }
    if (formState.percAlloc > maxPerc) {
      notifyUser('error', `Percentage cannot exceed ${maxPerc}% (available allocation)`);
      return;
    }

    // Assemble full name as: firstName + surname + otherNames (so original ordering is preserved when surname is the second token)
    const fullName = [formState.firstName, formState.surname, formState.otherNames].filter(Boolean).join(' ');

    const payload = {
      fullName,
      relationship: formState.relationship,
      birthDate: formState.birthDate ? dayjs(formState.birthDate).format('YYYY-MM-DD HH:mm:ss') : null,
      percAlloc: Number(formState.percAlloc),
      address: formState.address,
    };

    if (isEdit && beneficiaryToEdit) {
      updateBeneficiaryMutation.mutate({
        beneficiaryId: beneficiaryToEdit.beneficiaryId,
        ...payload,
      }, {
        onSuccess: () => {
          notifyUser('success', 'Beneficiary updated successfully');
          if (onEditSuccess) {
            onEditSuccess({
              beneficiaryId: beneficiaryToEdit.beneficiaryId,
              name: fullName,
              relationship: formState.relationship,
              dob: formState.birthDate,
              address: formState.address,
              percAllocValue: Number(formState.percAlloc),
              percentage: `${Number(formState.percAlloc)}%`,
            });
          }
          onClose();
        },
        onError: (error) => {
          notifyUser('error', error?.response?.data?.message || error?.message || 'Failed to update beneficiary');
        },
      });
    } else {
      addBeneficiaryMutation.mutate(payload, {
        onSuccess: () => {
          notifyUser('success', 'Beneficiary added successfully');
          setFormState({
            firstName: '',
            surname: '',
            otherNames: '',
            relationship: '',
            birthDate: '',
            percAlloc: 0,
            address: '',
          });
          onClose();
        },
        onError: (error) => {
          notifyUser('error', error?.response?.data?.message || error?.message || 'Failed to add beneficiary');
        },
      });
    }
  };

  const relationshipOptions = [
    'SPOUSE',
    'SON',
    'DAUGHTER',
    'PARENT',
    'FATHER',
    'MOTHER',
    'SIBLING',
    'NEPHEW',
    'NIECE',
    'BROTHER',
    'WIFE',
    'HUSBAND',
    'SISTER',
    'OTHER',
  ];

  const maxPerc = isEdit ? 100 - (currentTotalPerc - (beneficiaryToEdit?.percAllocValue || 0)) : 100 - currentTotalPerc;

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.18)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: modalWidth,
          maxHeight: '85vh',
          overflowY: 'auto',
          padding: 0,
          boxShadow: '0 16px 60px rgba(0,0,0,0.15)'
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 32px 20px 32px' }}>
          <div className="modal-header-title" style={{ fontWeight: 800, color: '#222', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{isEdit ? 'Edit Beneficiary' : 'Add Beneficiary'}</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#888', cursor: 'pointer', marginLeft: 12, fontWeight: 700, lineHeight: 1 }} aria-label="Close">×</button>
          </div>
          <p className="modal-header-desc" style={{ color: '#888', marginBottom: 0, marginTop: 0 }}>{isEdit ? `Update the details for ${beneficiaryToEdit?.name}.` : 'Add a new beneficiary to this policy.'}</p>
        </div>

        {/* Content */}
        <div style={{ padding: '0 32px 32px 32px' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <omk-text-field
              label="First Name"
              name="firstName"
              value={formState.firstName}
              onInput={handleFormChange('firstName')}
              required
              style={omkTextFieldStyles}
              placeholder="Enter first name"
            />
          </div>
          <div>
            <omk-text-field
              label="Surname"
              name="surname"
              value={formState.surname}
              onInput={handleFormChange('surname')}
              required
              style={omkTextFieldStyles}
              placeholder="Enter surname"
            />
          </div>
          <div>
            <omk-text-field
              label="Other Names"
              name="otherNames"
              value={formState.otherNames}
              onInput={handleFormChange('otherNames')}
              style={omkTextFieldStyles}
              placeholder="Enter other names"
            />
          </div>
          <div>
            <omk-select
              label="Relationship"
              value={formState.relationship}
              onInput={handleFormChange('relationship')}
            >
              {relationshipOptions.map((rel) => (
                <omk-select-option value={rel} key={rel}>
                  {rel}
                </omk-select-option>
              ))}
            </omk-select>
          </div>
          <div>
            <omk-date-picker
              label="Birth Date"
              name="birthDate"
              type="date"
              value={formState.birthDate}
              onInput={handleFormChange('birthDate')}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <omk-text-field
              label={`Percentage Allocation (%) - Available: ${maxPerc}%`}
              name="percAlloc"
              type="number"
              min="0"
              max={maxPerc}
              value={formState.percAlloc}
              onInput={handlePercentChange}
              required
              style={omkTextFieldStyles}
              placeholder={`Enter allocation percentage (0-${maxPerc})`}
            />
            {maxPerc === 0 && (
              <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                No available percentage allocation. Please reduce existing beneficiaries' allocations first.
              </div>
            )}
          </div>
          <div>
            <omk-text-field
              label="Address"
              name="address"
              value={formState.address}
              onInput={handleFormChange('address')}
              style={omkTextFieldStyles}
              placeholder="Enter address"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 4 }}>
            <button type="submit" className="btn btn-primary" disabled={addBeneficiaryMutation.isPending || updateBeneficiaryMutation.isPending}>
              {(addBeneficiaryMutation.isPending || updateBeneficiaryMutation.isPending) ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update Beneficiary' : 'Add Beneficiary')}
            </button>
            <button type="button" className="btn btn-white" onClick={onClose} style={{ padding: '10px 18px', fontWeight: 600 }}>Cancel</button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};